/**
 * From field for new Create Task modal.
 * Single-select picklist when user has active coverage; read-only current user otherwise.
 * Display value: name only (no avatar), matching To/CC chip style.
 * Dropdown list: avatar + First Last Name + title line (or "-"); scrollable.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSelector } from 'react-redux';
import { useActiveCoverageForTaskActions } from 'store/redux-store/user-coverage/useActiveCoverageForTaskActions';
import { selectUser } from 'oidc/user.selectors';
import { makeStyles } from 'tss-react/mui';
import { formatEmployeeName, getInitials } from 'app/components/Tasks/CreateTask/FormatData';
import { ICoverageDetails } from 'app/models/UserCoverage/UserCoverage';
import { getAllEmployees } from 'app/services/TaskServices/TaskServices';

export interface IFromOption {
  id: number | string;
  coveredEmployeeId: number;
  coveredEmployeeFirstName: string;
  coveredEmployeeLastName: string;
  title?: string;
  /** When set, this option is "on behalf of" a covered user; parent uses it to build taskCoverageAction. */
  coverageDetails?: ICoverageDetails;
}

type CoverageDetailsWithRole = ICoverageDetails & {
  coveredEmployeeRoleDescription?: string;
  roleDescription?: string;
  role?: string;
  employeeRoleName?: string;
  title?: string;
};

export interface FromFieldProps {
  value: IFromOption | null;
  onChange: (option: IFromOption) => void;
  disabled?: boolean;
  className?: string;
  displayClassName?: string;
  placeholder?: string;
}

const useStyles = makeStyles()(theme => ({
  root: {
    width: '100%',
  },
  displayBox: {
    minHeight: 40,
    padding: '8px 12px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 0,
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'default',
    boxSizing: 'border-box',
    '&:focus-within': {
      outline: 'none',
    },
  },
  displayBoxClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  displayBoxReadOnly: {
    cursor: 'default',
    backgroundColor: theme.palette.background.paper,
  },
  displayName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 400,
    fontSize: 14,
    color: theme.palette.text.primary
  },
  caret: {
    flexShrink: 0,
    color: theme.palette.action.active,
    marginLeft: 4,
  },
  innerContent: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 4,
    flexWrap: 'nowrap',
    width: 'fit-content',
    maxWidth: '100%',
  },
  menuPaper: {
    maxHeight: 320,
    minWidth: 280,
    borderTop: `3px solid ${theme.palette.primary.main}`,
  },
  menuItem: {
    padding: '10px 12px',
  },
  menuItemAvatar: {
    width: 32,
    height: 32,
    fontSize: '12px',
    marginRight: 12,
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
  },
  menuItemName: {
    fontWeight: 500,
    fontSize: 14,
  },
  menuItemTitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));

export const FromField = ({
  value,
  onChange,
  disabled = false,
  className,
  displayClassName,
  placeholder = 'Sender',
}: FromFieldProps) => {
  const { classes } = useStyles();
  const userInfo = useSelector(selectUser);
  const activeCoverage = useActiveCoverageForTaskActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [resolvedRolesByEmpId, setResolvedRolesByEmpId] = useState<Record<number, string>>({});
  const roleLookupAttemptedRef = useRef<Set<number>>(new Set());
  const open = Boolean(anchorEl);
  const currentUserId = userInfo?.userInfo?.employeeId ?? 0;

  const getRoleLabel = (coverage: CoverageDetailsWithRole | null | undefined): string => {
    if (!coverage) return '-';
    return (
      coverage.coveredEmployeeRoleDescription ??
      coverage.roleDescription ??
      coverage.employeeRoleName ??
      coverage.role ??
      coverage.title ??
      '-'
    );
  };

  const fromOptions: IFromOption[] = useMemo(() => {
    const currentUser: IFromOption = {
      id: currentUserId,
      coveredEmployeeId: currentUserId,
      coveredEmployeeFirstName: userInfo?.userInfo?.firstName ?? '',
      coveredEmployeeLastName: userInfo?.userInfo?.lastName ?? '',
      title: '-',
    };
    const coverageList = activeCoverage ?? [];
    if (coverageList.length === 0) {
      return [currentUser];
    }
    const list: IFromOption[] = [];
    const seen = new Set<number>();
    coverageList.forEach((cov: ICoverageDetails) => {
      if (cov.coveredEmployeeId != null && !seen.has(cov.coveredEmployeeId)) {
        const coverageWithRole = cov as CoverageDetailsWithRole;
        seen.add(cov.coveredEmployeeId);
        list.push({
          id: cov.coveredEmployeeId,
          coveredEmployeeId: cov.coveredEmployeeId,
          coveredEmployeeFirstName: cov.coveredEmployeeFirstName ?? '',
          coveredEmployeeLastName: cov.coveredEmployeeLastName ?? '',
          title: getRoleLabel(coverageWithRole),
          coverageDetails: cov,
        });
      }
    });
    return list;
  }, [currentUserId, userInfo?.userInfo?.firstName, userInfo?.userInfo?.lastName, activeCoverage]);

  const selectedFromId = (value?.coveredEmployeeId ?? currentUserId) || 0;
  const dropdownOptions = useMemo(
    () => fromOptions.filter(option => option.coveredEmployeeId !== selectedFromId),
    [fromOptions, selectedFromId],
  );

  useEffect(() => {
    let isMounted = true;
    const unresolved = dropdownOptions.filter(
      option =>
        option.coveredEmployeeId > 0 &&
        (!option.title || option.title === '-') &&
        !resolvedRolesByEmpId[option.coveredEmployeeId] &&
        !roleLookupAttemptedRef.current.has(option.coveredEmployeeId),
    );
    if (unresolved.length === 0) return;
    unresolved.forEach(option => roleLookupAttemptedRef.current.add(option.coveredEmployeeId));

    const resolveRoles = async () => {
      const nextMap: Record<number, string> = {};
      await Promise.all(
        unresolved.map(async option => {
          const nameQuery = `${option.coveredEmployeeFirstName ?? ''} ${option.coveredEmployeeLastName ?? ''}`.trim();
          if (!nameQuery) return;
          const results = await getAllEmployees(nameQuery);
          const match = (results ?? []).find((employee: any) => employee?.employeeId === option.coveredEmployeeId);
          const role = match?.employeeRoleName ?? '-';
          if (role && role !== '-') {
            nextMap[option.coveredEmployeeId] = role;
          }
        }),
      );
      if (isMounted && Object.keys(nextMap).length > 0) {
        setResolvedRolesByEmpId(prev => ({ ...prev, ...nextMap }));
      }
    };

    resolveRoles();
    return () => {
      isMounted = false;
    };
  }, [dropdownOptions, resolvedRolesByEmpId]);

  const hasCoverage = (activeCoverage?.length ?? 0) > 0;
  const isEditable = hasCoverage && !disabled && dropdownOptions.length > 0;
  const displayOption = value ?? fromOptions[0] ?? null;
  const rawDisplayName = displayOption
    ? formatEmployeeName(displayOption.coveredEmployeeFirstName, displayOption.coveredEmployeeLastName)
    : '';
  // Design: show first + last name only (no avatar). Never show email in the From field.
  const displayName =
    rawDisplayName?.trim() && !rawDisplayName.includes('@')
      ? rawDisplayName.trim()
      : placeholder;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isEditable) setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (option: IFromOption) => {
    onChange(option);
    handleClose();
  };

  return (
    <div className={className ?? classes.root}>
      <Box
        id="from-field-button"
        className={`${classes.displayBox} ${displayClassName ?? ''} ${isEditable ? classes.displayBoxClickable : classes.displayBoxReadOnly}`}
        onClick={handleClick}
        role={isEditable ? 'button' : undefined}
        aria-haspopup={isEditable ? 'listbox' : undefined}
        aria-expanded={open}
        data-testid="create-task-new-from-field"
      >
        <Box className={classes.innerContent}>
          <Typography className={classes.displayName} variant="body2" component="span">
            {displayName || placeholder}
          </Typography>
          {isEditable && (
            <KeyboardArrowDownIcon className={classes.caret} fontSize="small" />
          )}
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.menuPaper }}
        MenuListProps={{ 'aria-labelledby': 'from-field-button' }}
      >
        {dropdownOptions.map((option) => {
          const name = formatEmployeeName(option.coveredEmployeeFirstName, option.coveredEmployeeLastName);
          const isSelected = value?.id === option.id;
          return (
            <MenuItem
              key={option.id}
              selected={isSelected}
              onClick={() => handleSelect(option)}
              className={classes.menuItem}
            >
              <Avatar className={classes.menuItemAvatar}>
                {getInitials(name)}
              </Avatar>
              <ListItemText
                primary={name}
                secondary={resolvedRolesByEmpId[option.coveredEmployeeId] ?? option.title ?? '-'}
                primaryTypographyProps={{ className: classes.menuItemName }}
                secondaryTypographyProps={{ className: classes.menuItemTitle }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};
